import React, { useState, useRef } from 'react'
import classNames from 'classnames'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { FileInputProps } from '@toasttab/shared/src/interfaces/Interface.d'

export const validFileExtensions = [
  'doc',
  'docx',
  'pdf',
  'txt',
  'rtf',
  'png',
  'jpg',
  'jpeg'
]

const FileInput = ({
  disabled,
  error,
  formik,
  id,
  label = 'No file chosen',
  name,
  placeholder,
  required,
  variation = 'default'
}: FileInputProps) => {
  const [files, setFiles] = useState([])

  const ref = useRef<HTMLInputElement>(null)
  const { t } = useI18next()

  const handleFileChange = async (e: any) => {
    const uploadedFiles = await e.target.files
    if (e.target.files?.length > 0) {
      const newFiles = [...files]
      Array.from(uploadedFiles).forEach((currentFile: any, i: number) => {
        // Append timestamp to all files, so duplicate filenames can be properly uploaded
        const currentTime = new Date().toISOString().replace(/[.:]/g, '')
        const parts = currentFile.name?.split('.')
        const extension = parts.pop() // Remove and get the last part (extension)
        const baseName = parts.join('.')
        const newName = `${baseName}-${currentTime}.${extension}`

        // Create a new file object so we can change the name, as File is read-only
        const updatedFile = new File([currentFile], newName, {
          type: currentFile.type
        })
        const fileFormik = `file${files.length + i}`

        // Check if file exists to prevent duplicate uploads
        if (!formik.values[fileFormik]) {
          // Send the updated file to the formik data so it gets the timestamp value when uploaded
          formik.values[fileFormik] = updatedFile
        }
        // Check if the value exists in state and add it to the list
        if (!files.some((f) => f.name === currentFile.name)) {
          newFiles.push(currentFile)
          // Store the updated file list in state so we can reference it later
          setFiles(newFiles)
          formik.values[name] = newFiles
        }
      })
    }
  }

  const removeFile = (e: any, i: number) => {
    e.preventDefault()
    const newFiles = [...files].filter(
      (f) => f.name !== e.target.dataset.filename
    )
    setFiles(newFiles)
    formik.values[name] = newFiles
    delete formik.values[`file${i}`]
  }

  const labelClassNames = classNames(
    'p-4 cursor-pointer border border-gray-50 rounded-sm flex w-full items-center peer-focus/input:text-[10px] peer-focus/input:leading-[10px] relative',
    'text-left text-secondary tracking-[0.2px] mb-1 before:content-icon-upload before:h-6 before:w-6 before:block before:mr-[10px]'
  )

  const errorClassNames = classNames(
    'absolute top-full w-max flex flex-row font-sansPro items-center text-xs font-semibold left-0 leading-4 -mt-1 before:content-[""]',
    'before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:flex-shrink-0 before:block before:mr-2',
    {
      'text-error before:bg-icon-warning-outline': variation === 'default',
      'text-white before:bg-icon-warning-outline-inverse':
        variation === 'inverse'
    }
  )

  return (
    <div className='relative w-full [&~.text-xs.mt-4]:mt-1'>
      <label
        className={labelClassNames}
        data-testid='FileInput-label'
        htmlFor={id}
        role='button'
      >
        {label}
        <input
          aria-describedby={error ? `${name}-error` : undefined}
          aria-invalid={!!error}
          className='opacity-0 cursor-pointer absolute top-0 left-0 right-0 bottom-0 w-full h-full file:cursor-pointer'
          data-testid='FileInput-input'
          disabled={disabled}
          id={id}
          maxLength={255}
          multiple
          name={name}
          onChange={handleFileChange}
          placeholder={!label ? placeholder : null}
          ref={ref}
          required={required}
          type='file'
        />
      </label>
      <p className='text-xs text-default'>{`${t(
        'Maximum file size 1MB. Supported file types:'
      )} .${validFileExtensions.join(', .')}`}</p>
      {files?.map((f: any, i: number) => (
        <div className='w-full mb-1 text-default' key={f.name}>
          <button
            className='mr-1.5 pointer-events-auto before:content-icon-close before:inline-block before:h-[18px] before:w-[18px] before:relative before:top-1'
            data-filename={f.name}
            onClick={(e) => removeFile(e, i)}
          />
          {f.name}
        </div>
      ))}
      {files?.length > 0 ? (
        <input
          data-testid='FileInput-attachments'
          id='attachments'
          name='attachments'
          type='hidden'
          value={files.map((f) => f.name).join(', ')}
        />
      ) : null}
      {/* This uL field sets the folder ID for the attachments.
          Right now this is only used on the IRR form, so it is hard-coded to 40881.
          If we need different forms to upload to different locations, we can either build a function, or add it as an option in Contentful.
          */}
      <input id='uL' name='uL' type='hidden' value={40881} />
      {error ? (
        <div
          className={errorClassNames}
          data-testid='FileInput-error'
          id={`${name}-error`}
        >
          {error}
        </div>
      ) : null}
    </div>
  )
}

export default FileInput
